import Tippy from '@tippy.js/react';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TenancyOptionGroup from '~components/booking-process/select-bed/tenancy-option-group';
import Loading from '~components/button/loading';
import {
  AvailableTenancyOptionUnit,
  mapAvailableTenancyOptionsToList,
} from '~components/datepicker/tenancy-option-helpers';
import Typography from '~components/typography';
import { TenancyTypeOption } from '~graphql/__generated__/globalTypes';
import { useTranslation } from '~i18n';
import { IconJumpDown } from '~svg-components';
import styles from './tenancy-option-select.module.scss';

interface IDate {
  startDate: Date | undefined;
  endDate: Date | undefined;
}
interface ITenancyOptionSelectProps {
  availableTenancyOptions?: AvailableTenancyOptionUnit[] | null;
  tenancyOptionId: string;
  tenancyDate: IDate;
  disabled?: boolean;
  loading?: boolean;
  setAcademicYearId: (value: string) => void;
  setTenancyDate: (value: IDate) => void;
  setSelectTenancyType: (value: TenancyTypeOption) => void;
  setTenancyOptionId: (value: string) => void;
  setTenancyOptionRateType: (value: string) => void;
  setTenancyOptionLengthType: (value: string) => void;
  onFlexibleTenancySelect?: (params: {
    tenancyOptionId: string;
    startDate: string;
    endDate: string;
    selectableStartDates: string[];
  }) => void;
}

const TenancyOptionSelect: React.FC<ITenancyOptionSelectProps> = ({
  tenancyDate,
  tenancyOptionId,
  disabled,
  loading,
  setTenancyDate,
  setSelectTenancyType,
  setAcademicYearId,
  setTenancyOptionId,
  setTenancyOptionRateType,
  setTenancyOptionLengthType,
  availableTenancyOptions,
  onFlexibleTenancySelect,
}) => {
  const [t] = useTranslation('student_portal');
  const tenancyOptionSelectRef = useRef<any>(null);
  const [tippyInstance, setTippyInstance] = useState<any>(null);

  const [groupVisible, setGroupVisible] = useState(false);

  const tenancyOptionsList = useMemo(
    () => mapAvailableTenancyOptionsToList(availableTenancyOptions),
    [availableTenancyOptions]
  );

  const selectTenancy = useMemo(() => {
    let tenancyOption = null;
    for (let item of tenancyOptionsList) {
      const tenancyOptionItem = item.tenancyOptions.find(({ id }) => id === tenancyOptionId);
      if (tenancyOptionItem) {
        tenancyOption = tenancyOptionItem;
      }
    }
    return tenancyOption;
  }, [tenancyOptionId, tenancyOptionsList]);

  useEffect(() => {
    if (
      !tenancyDate.startDate &&
      selectTenancy &&
      selectTenancy?.tenancyType !== TenancyTypeOption.FLEXIBLE
    ) {
      setTenancyDate({
        startDate: new Date(selectTenancy?.startDate),
        endDate: new Date(selectTenancy?.endDate),
      });
    }
  }, [selectTenancy, setTenancyDate, tenancyDate.startDate]);

  const handleHideTippy = useCallback(
    (e: any) => {
      if (!tenancyOptionSelectRef?.current?.contains(e.target)) {
        tippyInstance?.hide();
      }
    },
    [tippyInstance]
  );

  useEffect(() => {
    if (window.document) window.document.addEventListener('click', handleHideTippy);
    return () => {
      window.document.removeEventListener('click', handleHideTippy);
    };
  }, [handleHideTippy]);

  return (
    <div className={styles.root} ref={tenancyOptionSelectRef}>
      <Typography component="div" variant="body3" fontWeight={600}>
        {t('select_bed.tenancy_length')}
      </Typography>
      <Tippy
        hideOnClick={'toggle'}
        visible={groupVisible}
        trigger="click"
        theme={'white'}
        appendTo="parent"
        animation="scale"
        onCreate={instance => {
          if (!tippyInstance) setTippyInstance(instance);
        }}
        onShow={() => {
          setGroupVisible(true);
        }}
        onHide={() => {
          setGroupVisible(false);
        }}
        content={
          <TenancyOptionGroup
            tenancyOptionList={tenancyOptionsList}
            selectedValue={tenancyOptionId}
            onSelect={({
              tenancyOptionId,
              tenancyOptionStartDate,
              tenancyOptionEndDate,
              academicYearId,
              tenancyOptionRateType,
              tenancyLengthOption,
              tenancyType,
              selectableStartDates,
            }) => {
              setGroupVisible(false);
              setSelectTenancyType(tenancyType);
              setTenancyOptionId(tenancyOptionId);
              setTenancyDate({
                startDate:
                  tenancyType !== TenancyTypeOption.FLEXIBLE ? tenancyOptionStartDate : undefined,
                endDate:
                  tenancyType !== TenancyTypeOption.FLEXIBLE ? tenancyOptionEndDate : undefined,
              });
              if (tenancyOptionRateType) setTenancyOptionRateType(tenancyOptionRateType);
              if (academicYearId) setAcademicYearId(academicYearId);
              if (tenancyLengthOption) setTenancyOptionLengthType(tenancyLengthOption);

              if (tenancyType === TenancyTypeOption.FLEXIBLE) {
                const selectedOption = availableTenancyOptions?.find(
                  item => item.tenancyOption.id === tenancyOptionId
                );
                if (selectedOption) {
                  onFlexibleTenancySelect?.({
                    tenancyOptionId,
                    startDate: selectedOption.tenancyOption.startDate,
                    endDate: selectedOption.tenancyOption.endDate,
                    selectableStartDates:
                      selectableStartDates ?? selectedOption.selectableStartDates,
                  });
                }
              }
            }}
          />
        }
      >
        <div
          className={classNames(styles.container, {
            [styles.containerActive]: groupVisible && !disabled,
            [styles.containerDisabled]: !!disabled,
          })}
          onClick={() => {
            if (disabled || loading) return;
            setGroupVisible(!groupVisible);
          }}
        >
          <div className={styles.title}>
            <div className={styles.tenancyOption}>
              {selectTenancy?.name && (
                <Typography component="div" variant="body3" className={styles.tenancyOptionName}>
                  {selectTenancy?.name}
                </Typography>
              )}

              {!selectTenancy?.name && (
                <Typography variant="body3" color="var(--color-purple_3)">
                  {t('select_length_of_stay')}
                </Typography>
              )}
            </div>
            {!!loading && <Loading color="secondary" className={styles.tenancyLoading} />}
            <IconJumpDown
              width="8"
              height="6"
              fill="var(--color-grey_99)"
              className={classNames(styles.icon, {
                [styles.iconActive]: groupVisible,
                [styles.iconDisable]: !!disabled,
              })}
            />
          </div>
        </div>
      </Tippy>
    </div>
  );
};

export default TenancyOptionSelect;
